import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Users, GitFork, Search, ExternalLink } from "lucide-react";


export default function Gallery() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery/strategies", { params: q ? { q } : {} });
      setRows(data);
    } catch { toast.error("Failed to load gallery"); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[var(--bg-dark)]" data-testid="gallery-page">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[var(--accent)] font-mono text-xs uppercase tracking-widest mb-2">
              <Users size={14}/> Community
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">Strategy Gallery</h1>
            <p className="text-[var(--text-muted)] mt-2 max-w-2xl">
              Public strategies shared by the community. Fork any of them into your library — you'll get a private editable copy you can tweak and deploy.
            </p>
          </div>
          <div className="relative w-full max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/>
            <input data-testid="gallery-search"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search by name or symbol…"
              className="input-dark w-full pl-9"/>
          </div>
        </header>

        {loading ? (
          <div className="text-[var(--text-muted)] font-mono">Loading gallery…</div>
        ) : rows.length === 0 ? (
          <div className="card-sharp p-10 text-center" data-testid="gallery-empty">
            <div className="font-display text-2xl text-white mb-1">No published strategies yet</div>
            <p className="text-[var(--text-muted)] text-sm">Be the first — publish one of yours from the Builder.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((r) => (
              <GalleryCard key={r.strategy_id} r={r} loggedIn={!!user}/>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


function GalleryCard({ r, loggedIn }) {
  const [busy, setBusy] = useState(false);
  const doFork = async () => {
    if (!loggedIn) { toast.error("Log in to fork this strategy"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/strategies/${r.strategy_id}/fork`);
      toast.success("Forked → " + data.name);
      window.location.href = `/builder/${data.strategy_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Fork failed"); }
    finally { setBusy(false); }
  };
  return (
    <article className="card-sharp p-5 flex flex-col gap-3" data-testid={`gallery-card-${r.strategy_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip">{(r.platform || "mt4").toUpperCase()}</span>
        <span className="chip">{r.symbol}</span>
        <span className="chip">{(r.timeframe || "").replace("PERIOD_","")}</span>
      </div>
      <h3 className="font-display text-xl text-white truncate">{r.name}</h3>
      <div className="text-xs font-mono text-[var(--text-muted)] flex items-center gap-2">
        by {r.author.name} · <GitFork size={11}/> {r.fork_count} fork{r.fork_count === 1 ? "" : "s"}
      </div>
      <div className="mt-auto flex gap-2 pt-3 border-t border-white/5">
        <Link to={`/s/${r.public_slug}`}
          data-testid={`gallery-view-${r.strategy_id}`}
          className="btn-outline flex-1 text-center flex items-center justify-center gap-2">
          <ExternalLink size={13}/> View
        </Link>
        <button data-testid={`gallery-fork-${r.strategy_id}`}
          onClick={doFork} disabled={busy}
          className="btn-primary flex-1 flex items-center justify-center gap-2">
          <GitFork size={13}/> {busy ? "…" : "Fork"}
        </button>
      </div>
    </article>
  );
}
